import type { SubscriptionForwarder } from '@urql/core';
import type { ExecutionResult } from 'graphql';
import type Pusher from 'pusher-js';

import type { ActiveSubscription } from '~/types/websocket';

import { usePusherConnectionStore } from '~/stores/pusherConnection';
import { ChannelStatus } from '~/types/websocket';

const MAX_SUBSCRIPTIONS = 100;
const SUBSCRIPTION_TIMEOUT_MS = 30000; // 30 seconds

export const useSubscriptionExchange = () => {
  const { apiUrl } = useCurrentCluster();
  const config = useRuntimeConfig();
  const route = useRoute();
  const { getAppTypeHeaders } = useAppTypeHeaders();
  const pusherConnection = usePusherConnection();
  const store = usePusherConnectionStore();
  const { $logErrorMessage, $logWarnMessage, $logInfoMessage } = useNuxtApp();

  // Lazy initialization of shared Pusher client
  let pusherClientPromise: Promise<Pusher> | null = null;
  let reconnectHandlerRegistered = false;

  const cleanupSubscription = (subId: string) => {
    const sub = store.getSubscription(subId);
    if (sub && pusherClientPromise) {
      void pusherClientPromise.then((client) => {
        if (sub.channelName) {
          client.unsubscribe(sub.channelName);
        }
      });
      store.removeSubscription(subId);
    }
  };

  const setupChannelForSubscription = async (
    sub: ActiveSubscription,
    pusherClient: Pusher,
    sendInitialResult: boolean,
  ) => {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), SUBSCRIPTION_TIMEOUT_MS);

    // Set status to subscribing/reconnecting
    store.updateSubscription(sub.id, {
      status: sendInitialResult ? ChannelStatus.Subscribing : ChannelStatus.Reconnecting,
    });

    try {
      // Fetch new channel from GraphQL endpoint
      const response = await fetch(`${apiUrl.value}/graphql`, {
        method: 'POST',
        credentials: 'include',
        signal: abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...getAppTypeHeaders(route.path),
        },
        body: JSON.stringify(sub.operation),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Check if subscription was cancelled while we were fetching
      if (!store.hasSubscription(sub.id)) {
        return;
      }

      const channelName = result?.extensions?.lighthouse_subscriptions?.channel;
      if (!channelName) {
        $logErrorMessage('Subscription: No channel in response', {
          operationName: sub.operationName,
        });
        store.updateSubscription(sub.id, { status: ChannelStatus.Error });
        cleanupSubscription(sub.id);
        sub.callbacks.error(new Error('No channel in subscription response'));
        return;
      }

      // Subscribe to Pusher channel
      const channel = pusherClient.subscribe(channelName);

      // Update subscription via store action for proper Vue reactivity
      // Also update subscribedAt on reconnection to reflect when the channel was re-established
      store.updateSubscription(sub.id, {
        channelName,
        channel,
        status: ChannelStatus.Active,
        ...(!sendInitialResult && { subscribedAt: Date.now() }),
      });

      channel.bind('lighthouse-subscription', (payload: { more: boolean; result: ExecutionResult }) => {
        if (payload.result?.errors) {
          sub.callbacks.error(payload.result.errors);
        }
        else if (payload.result) {
          sub.callbacks.next(payload.result);
        }

        if (!payload.more) {
          cleanupSubscription(sub.id);
          sub.callbacks.complete();
        }
      });

      // Log channel subscription errors
      channel.bind('pusher:subscription_error', (status: { type: string; error: string; status: number }) => {
        $logWarnMessage('Pusher channel subscription error', {
          channelName: sub.channelName,
          operationName: sub.operationName,
          errorType: status.type,
          errorMessage: status.error,
          statusCode: status.status,
        });
      });

      // Forward initial result (only on first setup, not on reconnection)
      if (sendInitialResult) {
        sub.callbacks.next(result);
      }
    }
    catch (err) {
      clearTimeout(timeoutId);
      store.updateSubscription(sub.id, { status: ChannelStatus.Error });
      cleanupSubscription(sub.id);

      if (err instanceof Error && err.name === 'AbortError') {
        $logWarnMessage('Subscription setup timeout', {
          operationName: sub.operationName,
          timeoutMs: SUBSCRIPTION_TIMEOUT_MS,
        });
      }
      else {
        $logWarnMessage('Subscription setup failed', {
          operationName: sub.operationName,
          error: err,
        });
      }
      sub.callbacks.error(err);
    }
  };

  const markAllChannelsDisconnected = () => {
    const subscriptions = store.getAllSubscriptions();
    for (const sub of subscriptions) {
      if (sub.status === ChannelStatus.Active) {
        store.updateSubscription(sub.id, { status: ChannelStatus.Disconnected });
      }
    }
  };

  const resubscribeAll = async (pusherClient: Pusher) => {
    const subscriptions = store.getAllSubscriptions();
    if (subscriptions.length === 0) {
      return;
    }

    $logInfoMessage('Pusher resubscribing after reconnection', {
      count: subscriptions.length,
      operations: subscriptions.map(s => s.operationName),
    });

    // Unsubscribe from old channels (they're invalid after reconnection)
    for (const sub of subscriptions) {
      if (sub.channelName) {
        pusherClient.unsubscribe(sub.channelName);
      }
    }

    // Re-establish subscriptions with fresh channels from the backend
    await Promise.all(
      subscriptions.map(sub =>
        setupChannelForSubscription(sub, pusherClient, false),
      ),
    );
  };

  const getGraphqlPusherClient = async (): Promise<Pusher> => {
    if (!pusherConnection) {
      throw new Error('SSR context');
    }

    if (!pusherClientPromise) {
      const pusherHost = <string>config.public.pusherHost;
      const pusherPort = Number(config.public.pusherPort);
      const pusherScheme = <string>config.public.pusherScheme;
      if (!pusherHost || !pusherPort || !pusherScheme) {
        throw new Error('NUXT_PUBLIC_PUSHER_HOST / NUXT_PUBLIC_PUSHER_PORT / NUXT_PUBLIC_PUSHER_SCHEME are not configured');
      }
      pusherClientPromise = pusherConnection.initialize({
        appKey: config.public.pusherAppKey,
        cluster: 'us2', // We dont actually use pusher servers, this value doesnt do anything, but is required
        wsHost: pusherHost,
        wsPort: pusherPort,
        forceTLS: pusherScheme === 'https',
        enabledTransports: ['wss', 'ws'],
        disableStats: true,
        activityTimeout: 20000,
        pongTimeout: 10000,
        authorizer: (channel) => {
          return {
            authorize: async (socketId, callback) => {
              try {
                const response = await fetch(`${apiUrl.value}/graphql/subscriptions/auth`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...getAppTypeHeaders(route.path),
                  },
                  body: new URLSearchParams({
                    socket_id: socketId,
                    channel_name: channel.name,
                  }),
                });

                if (!response.ok) {
                  $logWarnMessage('Pusher channel authorization failed', {
                    channelName: channel.name,
                    statusCode: response.status,
                    statusText: response.statusText,
                  });
                  callback(new Error(`Auth failed: ${response.status}`), null);
                  return;
                }

                const data = await response.json();
                callback(null, data);
              }
              catch (error) {
                $logWarnMessage('Pusher channel authorization error', {
                  channelName: channel.name,
                  error,
                });
                callback(error as Error, null);
              }
            },
          };
        },
      });

      // Register reconnection handler (only once, during first initialization)
      if (!reconnectHandlerRegistered) {
        reconnectHandlerRegistered = true;
        const pusherClient = await pusherClientPromise;

        // Watch for disconnection to update channel statuses
        watch(
          () => pusherConnection.connectionState.value,
          (newState, oldState) => {
            if (oldState === 'connected' && newState !== 'connected') {
              markAllChannelsDisconnected();
            }
          },
        );

        pusherConnection.onReconnected(() => {
          // Add small delay to ensure Pusher's new connection is fully stable
          // The old WebSocket might still be closing when state changes to 'connected'
          setTimeout(() => {
            void resubscribeAll(pusherClient);
          }, 100);
        });
      }
    }

    return await pusherClientPromise;
  };

  const forwardSubscriptionToPusher: SubscriptionForwarder = (operation) => {
    return {
      subscribe: ({ next, error, complete }) => {
        // Check subscription limit
        if (store.getSubscriptionCount() >= MAX_SUBSCRIPTIONS) {
          const err = new Error(`Subscription limit reached (${MAX_SUBSCRIPTIONS})`);
          $logErrorMessage('Subscription limit exceeded', {
            operationName: operation.operationName,
            currentCount: store.getSubscriptionCount(),
            maxCount: MAX_SUBSCRIPTIONS,
          });
          error(err);
          return { unsubscribe: () => {} };
        }

        const subId = store.generateSubscriptionId();

        // Create subscription record
        const sub: ActiveSubscription = {
          id: subId,
          channelName: null,
          operationName: operation.operationName,
          subscribedAt: Date.now(),
          status: ChannelStatus.Pending,
          operation: {
            query: typeof operation.query === 'string' ? operation.query : '',
            variables: operation.variables as Record<string, unknown> | undefined,
            operationName: operation.operationName,
          },
          callbacks: { next, error, complete },
          channel: null,
        };

        store.addSubscription(sub);

        const setup = async () => {
          try {
            const pusherClient = await getGraphqlPusherClient();
            await setupChannelForSubscription(sub, pusherClient, true);
          }
          catch (err) {
            cleanupSubscription(subId);
            error(err);
          }
        };

        void setup();

        return {
          unsubscribe: () => {
            cleanupSubscription(subId);
          },
        };
      },
    };
  };

  return {
    forwardSubscriptionToPusher,
    getActiveSubscriptions: () => store.getAllSubscriptions().map(s => ({
      channelName: s.channelName,
      operationName: s.operationName,
      subscribedAt: s.subscribedAt,
    })),
  };
};
